import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { HasProduct, withProduct } from "@insite/client-framework/Components/ProductContext";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import * as React from "react";
import { css } from "styled-components";

/* Custom Code */
import getColor from "@insite/mobius/utilities/getColor";
import breakpoint from "@store/Framework/Common/Utilities/breakpoint";
/* Custom Code */

type Props = WidgetProps & HasProduct;

export interface ProductDetailsAttributesStyles {
    wrapper?: InjectableCss;
    attributeTypeLabelText?: TypographyPresentationProps;
    attributeTypeValuesText?: TypographyPresentationProps;
    // Custom Code
    list?: TypographyPresentationProps;
    item?: TypographyPresentationProps;
    noResult?: TypographyPresentationProps;
}

export const attributesStyles: ProductDetailsAttributesStyles = {
    attributeTypeLabelText: {
        weight: "inherit",
        css: css`
            width: calc(50% - 0.5em);
            font-weight: ${x => x.theme.design.typography.weight.bold};
        `,
    },
    attributeTypeValuesText: {
        css: css`
            width: calc(50% - 0.5em);
        `,
    },
    list: {
        size: "inherit",
        css: css`
            display: flex;
            flex-flow: row wrap;
            justify-content: space-between;
            font-size: ${x => x.theme.design.typography.size.smaller};
            word-wrap: break-word;
        `,
    },
    item: {
        css: css`
            display: flex;
            justify-content: space-between;
            width: 100%;
            padding: 1em 1.25em;
            background-color: ${getColor("common.background")};

            ${breakpoint("sm", "down")} {
                &:nth-child(odd) {
                    background-color: ${getColor("common.accent")};
                }
            }

            ${breakpoint("md")} {
                width: calc(50% - 10px);

                &:nth-child(4n + 1),
                &:nth-child(4n + 2) {
                    background-color: ${getColor("common.accent")};
                }
            }
        `,
    },
    noResult: {
        forwardAs: "p",
    },
};

const styles = attributesStyles;

const ProductDetailsAttributes: React.FC<Props> = ({ product: { brand, attributeTypes } }) => {
    if (!brand && (!attributeTypes || attributeTypes.length === 0)) {
        /* Custom Code */
        return <Typography {...styles.noResult}>{translate("There are no specifications available.")}</Typography>;
        /* Custom Code */
    }
    /* Removed - limitedAttributeTypes */

    /* Custom Code */
    return (
        <Typography as="ul" {...styles.list} data-test-selector="productDetails_attributes">
            {brand && (
                <Typography as="li" data-test-selector="brand_item" {...styles.item}>
                    <Typography data-test-selector="brand_item_label" {...styles.attributeTypeLabelText}>
                        {translate("Brand")}
                    </Typography>
                    <Typography data-test-selector="brand_item_value" {...styles.attributeTypeValuesText}>
                        {brand.name}
                    </Typography>
                </Typography>
            )}
            {attributeTypes &&
                attributeTypes.map(attributeType => (
                    <Typography
                        as="li"
                        key={attributeType.id.toString()}
                        data-test-selector="attributes_item"
                        data-attributetypeid={attributeType.id}
                        {...styles.item}
                    >
                        <Typography data-test-selector="attributes_item_label" {...styles.attributeTypeLabelText}>
                            {attributeType.label || attributeType.name}
                        </Typography>
                        <Typography data-test-selector="attributes_item_value" {...styles.attributeTypeValuesText}>
                            {(attributeType.attributeValues ?? [])
                                .map(attributeValue => attributeValue.valueDisplay)
                                .join(", ")}
                        </Typography>
                    </Typography>
                ))}
        </Typography>
    );
    /* Custom Code */
};

const widgetModule: WidgetModule = {
    component: withProduct(ProductDetailsAttributes),
    definition: {
        displayName: "Attributes",
        group: "Product Details",
        allowedContexts: ["ProductDetailsPage"],
    },
};

export default widgetModule;
